import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, Search, Menu, X, Mail } from 'lucide-react';
import axios from 'axios';
import { FEATURES } from '../config/features';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogList = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('idle');

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, [selectedTag]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = selectedTag ? `?tag=${selectedTag}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts${params}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/tags`);
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribeStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/blog/newsletter/subscribe`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus('idle'), 5000);
    } catch (error) {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus('idle'), 5000);
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="home-container">
      {/* Same Header as Homepage */}
      <header className="dark-header">
        <div className="logo-container">
          <Link to="/" className="logo-text" style={{ textDecoration: 'none' }}>WestBay</Link>
        </div>
        
        <nav className="dark-nav">
          <button onClick={() => navigate('/')} className="dark-nav-link">Home</button>
          <button onClick={() => navigate('/#services')} className="dark-nav-link">Services</button>
          <button onClick={() => navigate('/#about')} className="dark-nav-link">About</button>
          <button onClick={() => navigate('/#poser-ai')} className="dark-nav-link">Poser AI™</button>
          <button className="dark-nav-link" style={{ background: '#FF6B35', color: '#fff' }}>Blog</button>
        </nav>

        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="mobile-nav-link">Home</button>
          <button onClick={() => { navigate('/#services'); setMobileMenuOpen(false); }} className="mobile-nav-link">Services</button>
          <button onClick={() => { navigate('/#about'); setMobileMenuOpen(false); }} className="mobile-nav-link">About</button>
          <button onClick={() => { navigate('/#poser-ai'); setMobileMenuOpen(false); }} className="mobile-nav-link">Poser AI™</button>
          <button className="mobile-nav-link" style={{ background: '#FF6B35', color: '#fff' }}>Blog</button>
        </div>
      )}

      {/* Blog Hero - Pop Art Style */}
      <section className="blog-hero-section">
        <div className="blog-hero-content">
          <h1 className="blog-hero-title">WestBay Blog</h1>
          <p className="blog-hero-subtitle">Insights on Cloud Security, AI & Technology</p>
        </div>
      </section>

      {/* Blog Main Content */}
      <section className="blog-main-section">
        <div className="blog-layout">
          {/* Sidebar */}
          <aside className="blog-sidebar-pop">
            {/* Search */}
            <div className="sidebar-card">
              <h3>Search</h3>
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Topics */}
            <div className="sidebar-card">
              <h3>Topics</h3>
              <div className="topic-tags">
                <button
                  className={`topic-tag ${!selectedTag ? 'active' : ''}`}
                  onClick={() => setSelectedTag(null)}
                >
                  All Posts
                </button>
                {tags.map(tag => (
                  <button
                    key={tag.name}
                    className={`topic-tag ${selectedTag === tag.name ? 'active' : ''}`}
                    onClick={() => setSelectedTag(tag.name)}
                  >
                    {tag.name} ({tag.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Newsletter */}
            <div className="sidebar-card newsletter-card">
              <h3>Stay Updated</h3>
              <p>Get the latest insights delivered to your inbox.</p>
              {subscribeStatus === 'success' ? (
                <div className="subscribe-success">Thanks for subscribing!</div>
              ) : (
                <form onSubmit={handleSubscribe} className="subscribe-form">
                  <div className="subscribe-input">
                    <Mail size={18} />
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" disabled={subscribeStatus === 'loading'}>
                    {subscribeStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              )}
            </div>
          </aside>

          {/* Posts */}
          <div className="blog-posts-grid">
            {loading ? (
              <div className="blog-loading">Loading posts...</div>
            ) : filteredPosts.length === 0 ? (
              <div className="blog-empty">
                <h3>No posts yet</h3>
                <p>Check back soon for new content!</p>
              </div>
            ) : (
              filteredPosts.map(post => (
                <article key={post.id} className="blog-post-card">
                  {post.featured_image && (
                    <div className="post-card-image">
                      <img src={post.featured_image} alt={post.title} />
                    </div>
                  )}
                  <div className="post-card-content">
                    <div className="post-card-meta">
                      <span><Calendar size={14} /> {formatDate(post.created_at)}</span>
                      <span><User size={14} /> {post.author}</span>
                    </div>
                    <h2><Link to={`/blog/${post.slug}`}>{post.title}</Link></h2>
                    <p>{post.excerpt}</p>
                    <div className="post-card-tags">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="post-tag-chip">
                          <Tag size={12} /> {tag}
                        </span>
                      ))}
                    </div>
                    <Link to={`/blog/${post.slug}`} className="read-more-btn">
                      Read More →
                    </Link>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Floating Contact Button */}
      <button 
        className="floating-chat-button" 
        onClick={() => navigate('/#contact')}
        aria-label="Contact Us"
      >
        <Mail size={24} />
        <span className="floating-button-text">Contact</span>
      </button>

      {/* Footer - Same as Homepage */}
      <footer className="footer">
        <div className="footer-single-line">
          <div className="footer-brand">
            <div className="logo-container">
              <span className="logo-text">WestBay</span>
            </div>
          </div>
          <div className="footer-center">
            <p className="body-small">© 2026 WestBay Group LLC. All rights reserved.</p>
            <span className="footer-separator">|</span>
            <p className="footer-patent"><span className="patent-text">Patent Pending</span></p>
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy" className="footer-link">Privacy</Link>
            <Link to="/terms-of-service" className="footer-link">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogList;
